import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import type { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { FUNDING_APPLICATION_REVIEW_AIR_API, FUNDING_APPLICATION_REVIEW_DETAILS_API } from "../../components/constants";

dayjs.extend(relativeTime);

export interface FundingApplicationReviewDetailsElapsedTime
  extends FundingApplicationSchemas.FundingApplicationReviewDetails {
  elapsedTime: string | null;
  elapsedSeconds: number | null;
}

export const useInReviewDetails = (customerDomain: string, ready = true) => {
  const client = useAxios();
  const url = FUNDING_APPLICATION_REVIEW_DETAILS_API.replace(":customerSlug", customerDomain);
  const queryFn = () => client.get<FundingApplicationSchemas.FundingApplicationReviewDetails>(url);

  return useQuery({
    queryKey: [url],
    queryFn,
    enabled: ready,
    select: ({ data }): FundingApplicationReviewDetailsElapsedTime => {
      return {
        ...data,
        elapsedTime: data.submittedDatetime ? dayjs(data.submittedDatetime).fromNow(true) : null,
        elapsedSeconds: data.submittedDatetime
          ? Math.max(dayjs().diff(dayjs(data.submittedDatetime), "second"), 1)
          : null,
      };
    },
    refetchOnMount: true,
    refetchInterval: 10000,
  });
};

export const useInReviewStepStatus = (url: string) => {
  const client = useAxios();
  const queryFn = () => client.get<FundingApplicationSchemas.FundingApplicationReviewState>(url);
  return useSuspenseQuery({
    queryKey: [url],
    queryFn,
    select: ({ data }): FundingApplicationSchemas.FundingApplicationReviewState => data,
    refetchOnMount: true,
    refetchInterval: 10000,
  });
};

export const useAIRDetails = (customerDomain: string, airId: string) => {
  const client = useAxios();
  const airUrl = FUNDING_APPLICATION_REVIEW_AIR_API.replace(":customerSlug", customerDomain) + "?airId=" + airId;
  return useQuery({
    queryKey: [airUrl],
    queryFn: () => client.get<FundingApplicationSchemas.AIRSchema>(airUrl),
    select: ({ data }): FundingApplicationSchemas.AIRSchema => data,
    refetchOnMount: true,
    refetchInterval: 30000,
  });
};
